import { cn } from '../../utils/cn';
import React from 'react';

type BaseInputProps = {
  type?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  value?: string | number | readonly string[];
  className?: string;
};

type InputComponent = {
  as?: 'input';
} & React.InputHTMLAttributes<HTMLInputElement> & BaseInputProps;

type TextAreaComponent = {
  as: 'textarea';
  rows?: number;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement> & BaseInputProps;

type InputProps = InputComponent | TextAreaComponent;

export const Input = ({ 
  className, 
  as: Component = 'input',
  type,
  onChange,
  value,
  ...props 
}: InputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (type === 'number' && onChange) {
      const inputValue = e.target.value;
      
      // Handle decimal numbers
      if (inputValue.includes('.')) {
        const [wholePart, decimalPart] = inputValue.split('.');
        // Remove leading zeros from whole part unless it's just "0"
        const formattedWhole = wholePart.replace(/^0+(?=\d)/, '');
        // Combine with decimal part if it exists
        const numValue = decimalPart !== undefined 
          ? `${formattedWhole || '0'}.${decimalPart}`
          : formattedWhole;

        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: numValue
          }
        };
        onChange(syntheticEvent);
        return;
      }

      // Handle whole numbers
      const numValue = inputValue.replace(/^0+(?=\d)/, '');
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: numValue
        }
      };
      onChange(syntheticEvent);
      return;
    }
    onChange?.(e);
  };

  const inputProps = {
    className: cn(
      "w-full pl-4 pr-3 py-2 border-2 border-gray-200 rounded-xl",
      "focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100/50",
      "text-sm transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white",
      className
    ),
    type,
    onChange: handleChange,
    value,
    ...props
  };

  return Component === 'textarea' 
    ? <textarea {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} />
    : <input {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)} />;
}; 